import type {
  Admin,
  GroupedInterviewResult,
  HiddenResultPreview,
  InterviewResult,
} from '@recruitee/interview-results-types';
import type { OfferBrief } from '@recruitee/offers-types';

import { calculateRating } from './candidate.helpers';

export type InterviewResultsGroup = {
  items: InterviewResult[];
  title: string | null;
  rating: number | null;
  position?: string;
  hiddenResultsPreview?: HiddenResultPreview[];
  offer?: OfferBrief | null;
};
export const groupItemsBy = (
  groupBy: string,
  items: InterviewResult[],
  placementIds: number[],
  hiddenResultsPreview?: HiddenResultPreview[],
): GroupedInterviewResult[] => {
  switch (groupBy) {
    case 'stage':
      return groupByStage(items, placementIds, hiddenResultsPreview);

    case 'member':
      return groupByMember(items, hiddenResultsPreview);

    case 'form':
      return groupByForm(items, hiddenResultsPreview);

    default:
      return [];
  }
};

// eslint-disable-next-line max-lines-per-function
export const groupByStage = (
  results: InterviewResult[],
  placementIds: number[],
  hiddenResultsPreview?: HiddenResultPreview[],
): GroupedInterviewResult[] => {
  const groupedResults = (results || []).reduce(
    (acc, item) => {
      const key = item.offer && item.stage ? item.offer.id + '.' + item.stage.id : 'null';
      const value = acc[key] || [];
      acc[key] = [...value, item];
      return acc;
    },
    <Record<string, InterviewResult[]>>{},
  );

  const hiddenResultsPreviewReduced = (hiddenResultsPreview || []).reduce(
    (acc, item) => {
      const key = item.offer?.id + '.' + item.stage?.id || 'null';
      const value = acc[key] || [];
      acc[key] = [...value, item];
      return acc;
    },
    <Record<string, HiddenResultPreview[]>>{},
  );

  const groupedResultsMapped: InterviewResultsGroup[] = Object.entries(groupedResults).map(
    ([stageId, group]) => {
      const [item] = group;
      return {
        items: group,
        title: stageId === 'null' ? null : item.stage!.name,
        offer: stageId === 'null' ? null : item.offer,
        position: <string>(<unknown>placementIds.indexOf(<number>item.stageId)!),
        rating: calculateRating(group),
      };
    },
  );

  const hiddenResultsPreviewMapped = Object.entries(hiddenResultsPreviewReduced).map(
    ([stageId, group]) => {
      const [firstHiddenResultPreview] = group;
      return {
        items: [],
        hiddenResultsPreview: group,
        title: stageId === 'null' ? null : firstHiddenResultPreview.stage!.name,
        offer: stageId === 'null' ? null : firstHiddenResultPreview.offer,
        position: <string>(
          (<unknown>placementIds.indexOf(<number>firstHiddenResultPreview.stageId)!)
        ),
        rating: null,
      };
    },
  );

  const usedHiddenResultsPreviewIndices: number[] = [];

  hiddenResultsPreviewMapped.forEach((hiddenResultsGroup, index) => {
    const groupIndex = groupedResultsMapped.findIndex(
      group =>
        group.offer?.id === hiddenResultsGroup.offer?.id &&
        group.title === hiddenResultsGroup.title,
    );

    if (groupIndex > -1) {
      groupedResultsMapped[groupIndex] = {
        ...groupedResultsMapped[groupIndex],
        hiddenResultsPreview: hiddenResultsGroup.hiddenResultsPreview,
      };

      usedHiddenResultsPreviewIndices.push(index);
    }
  });

  const hiddenResultsPreviewWithoutUsed = hiddenResultsPreviewMapped.filter(
    (_, index) => !usedHiddenResultsPreviewIndices.includes(index),
  );

  const groupedResultsWithStage = groupedResultsMapped.slice(0, groupedResultsMapped.length - 1);
  const groupedResultsWithoutStage = groupedResultsMapped.slice(groupedResultsMapped.length - 1);
  const sortedResults = [...hiddenResultsPreviewWithoutUsed, ...groupedResultsWithStage].sort(
    (a, b) => (a.position! > b.position! ? 1 : -1),
  );

  return [...sortedResults, ...groupedResultsWithoutStage];
};

export const groupByForm = (
  results: InterviewResult[],
  hiddenResultsPreview?: HiddenResultPreview[],
): GroupedInterviewResult[] => {
  const groupedResults = results.reduce(
    (acc, item) => {
      const value = acc[item.interviewTemplateName || 'null'] || [];
      acc[item.interviewTemplateName] = [...value, item];
      return acc;
    },
    <Record<string, InterviewResult[]>>{},
  );

  const hiddenResultsPreviewMapped = {
    items: [],
    hiddenResultsPreview: hiddenResultsPreview || undefined,
    title: '',
    rating: null,
    position: '-1',
  };

  return [
    hiddenResultsPreviewMapped,
    ...Object.entries(groupedResults)
      .map(([interviewTemplateName, group]) => {
        const [item] = group;
        return {
          items: group,
          title: interviewTemplateName,
          position: item.interviewTemplateName ? interviewTemplateName : '0',
          rating: calculateRating(group),
        };
      })
      .sort((a, b) => (a.position > b.position ? 1 : -1)),
  ];
};

export const groupByMember = (
  results: InterviewResult[],
  hiddenResultsPreview?: HiddenResultPreview[],
): GroupedInterviewResult[] => {
  const DELETED_USER_ID = -1;
  const groupedResults = results.reduce(
    (acc, item) => {
      const id = item.guest?.id || item.adminId || item.admin?.id || DELETED_USER_ID;
      const value = acc[id] || [];
      acc[id] = [...value, item];
      return acc;
    },
    <Record<number, InterviewResult[]>>{},
  );

  const hiddenResultsPreviewMapped = {
    items: [],
    hiddenResultsPreview: hiddenResultsPreview || undefined,
    title: '',
    titleTranslationKey: 'app.interview.hidden_evaluations',
    rating: null,
  };

  const groupedResultsMapped = Object.values(groupedResults).map(groupMapFn);

  return [
    hiddenResultsPreviewMapped,
    ...groupedResultsMapped.sort((a, b) => (a.title > b.title ? 1 : -1)),
  ];
};

export const filterResults = (offerId: string | number | undefined, results: InterviewResult[]) => {
  if (offerId === 'all') {
    return results;
  }

  return results.filter(res => (res.offerId || res.talentPoolId) === offerId);
};

const groupMapFn = (group: InterviewResult[]) => {
  const [item] = group;
  let name = '';
  if ((!item.admin || item.admin.anonymizedAt) && !item.guest) {
    return {
      items: group,
      title: '',
      titleTranslationKey: 'app.common.deleted_user',
      rating: calculateRating(group),
    };
  } else {
    if (item.admin) {
      name = item.admin.firstName
        ? `${item.admin.firstName} ${item.admin.lastName}`
        : (<Admin>item.admin).name;
    } else if (item.guest) {
      name = item.guest.name;
    }

    return {
      items: group,
      title: name,
      rating: calculateRating(group),
    };
  }
};
